import { useCallback } from 'react';
import { cn } from '@/lib/cn';
import type { KpiTone } from './KpiTile';

/**
 * A plain filled track — the 2026-10 dashboard redesign's "Completed: 20
 * (100%)" bar and the wait-time breakdown's per-role bars
 * (docs/design/dashboard). Optionally two-part: a lighter `leadPct` segment
 * in front of the solid `pct` one (the wait-time card's "lighter = waiting,
 * solid = working").
 *
 * Widths go through CSS custom properties set by a ref callback, not an
 * inline `style` prop — `components/ui/**` bans new inline styles; same
 * pattern as MarkerProgressBar.
 */
export interface ProgressBarProps {
  /** 0-100, the solid segment. */
  pct: number;
  /** 0-100, an optional lighter segment drawn before the solid one. */
  leadPct?: number;
  /** `success` (default) for completion, or a categorical tone for identity bars. */
  tone?: 'success' | KpiTone;
  className?: string;
  /** Accessible name, e.g. "Completed visits". */
  label?: string;
}

const FILL: Record<NonNullable<ProgressBarProps['tone']>, string> = {
  success: 'bg-success', blue: 'bg-tone-blue', orange: 'bg-tone-orange', violet: 'bg-tone-violet',
  green: 'bg-tone-green', red: 'bg-tone-red', teal: 'bg-tone-teal', indigo: 'bg-tone-indigo', slate: 'bg-tone-slate',
};

const clamp = (n: number) => Math.max(0, Math.min(100, Number.isFinite(n) ? n : 0));

export function ProgressBar({ pct, leadPct = 0, tone = 'success', className, label }: ProgressBarProps) {
  const lead = clamp(leadPct);
  const solid = Math.min(clamp(pct), 100 - lead);
  const ref = useCallback((el: HTMLElement | null) => {
    if (!el) return;
    el.style.setProperty('--lead-pct', `${lead}%`);
    el.style.setProperty('--solid-pct', `${solid}%`);
  }, [lead, solid]);
  return (
    <div
      ref={ref}
      role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(lead + solid)}
      className={cn('flex h-1.5 overflow-hidden rounded-full bg-muted', className)}
    >
      {lead > 0 && <div className={cn('h-full w-[var(--lead-pct)] opacity-45', FILL[tone])} />}
      <div className={cn('h-full w-[var(--solid-pct)] transition-[width] duration-200', FILL[tone])} />
    </div>
  );
}
